import React, { useState } from 'react';
import { 
  Row, Col, Card, Tabs, List, Tag, Badge, Button, Space, Input, 
  Select, DatePicker, Steps, Timeline, Radio, Divider, Avatar, Progress 
} from 'antd';
import { 
  SearchOutlined, FilterOutlined, BellOutlined, CheckCircleOutlined, 
  SyncOutlined, ExclamationCircleOutlined, ClockCircleOutlined, 
  UserOutlined, ShareAltOutlined, DownloadOutlined, SolutionOutlined,
  FireOutlined, LineChartOutlined 
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import './index.css';

const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Step } = Steps;

// 模拟数据
const mockWarnings = [
  {
    id: 1,
    title: '企业开办时长突增',
    type: '指标异常',
    level: 'urgent',
    area: '城东区',
    createTime: '2023-10-15 10:23:45',
    status: 'pending',
    department: '行政审批局',
    desc: '近期城东区企业开办平均时长由原来的3.2天增长至5.7天，增幅超过70%，已达到红色预警阈值。',
    influence: '影响约125家新办企业，可能导致企业满意度下降和负面舆情风险。',
    suggestion: '建议行政审批局增派人手处理积压申请，并简化特定行业审批流程。'
  },
  {
    id: 2,
    title: '多家企业反映行政审批延迟',
    type: '投诉反馈',
    level: 'high',
    area: '西湖区',
    createTime: '2023-10-14 16:35:22',
    status: 'processing',
    department: '行政审批局',
    desc: '西湖区近10家企业集中反映工程建设项目审批时间过长，平均延迟15个工作日。',
    influence: '影响区域建筑业发展，预计损失约300万元产值。',
    suggestion: '建议审批局开展专项调查，针对反映问题进行流程优化，并设立绿色通道。'
  },
  {
    id: 3,
    title: '社交媒体出现负面评价讨论',
    type: '舆情监测',
    level: 'normal',
    area: '全市',
    createTime: '2023-10-14 09:12:56',
    status: 'done',
    department: '营商环境办',
    desc: '多个社交平台出现关于本市营商环境的负面讨论，集中于政务服务效率和透明度问题。',
    influence: '可能影响城市形象和招商引资工作。',
    suggestion: '建议营商环境办收集具体问题点，组织相关部门召开专题会议研究解决方案。'
  },
  {
    id: 4,
    title: '营商环境指数下降趋势明显',
    type: '系统预警',
    level: 'high',
    area: '河北区',
    createTime: '2023-10-13 11:42:17',
    status: 'pending',
    department: '营商环境办',
    desc: '河北区营商环境指数连续3个月下降，累计降幅达12.5%，低于全市平均水平。',
    influence: '影响区域经济活力，可能导致企业迁出或减少投资。',
    suggestion: '建议对标先进区域，找出差距，制定专项提升计划。'
  },
  {
    id: 5,
    title: '企业融资难问题集中反映',
    type: '投诉反馈',
    level: 'normal',
    area: '南山区',
    createTime: '2023-10-12 15:37:28',
    status: 'processing',
    department: '金融局',
    desc: '南山区科技型中小企业集中反映融资困难，贷款审批时间长，担保要求高。',
    influence: '影响约50家创新型企业发展，可能导致部分项目流产。',
    suggestion: '建议金融局联合本地银行开展科技企业专项融资对接会，设立快速审批通道。'
  },
];

// 模拟处理流程数据
const mockProcessSteps = [
  {
    title: '预警生成',
    description: '系统自动预警',
    time: '2023-10-15 10:23:45',
    status: 'finish',
    user: '系统'
  },
  {
    title: '预警分派',
    description: '已分派至行政审批局',
    time: '2023-10-15 10:30:12',
    status: 'finish',
    user: '王管理员'
  },
  {
    title: '处理中',
    description: '正在组织人员解决',
    time: '2023-10-15 11:15:27',
    status: 'process',
    user: '李科长'
  },
  {
    title: '审核确认',
    description: '待确认问题解决情况',
    time: '',
    status: 'wait',
    user: ''
  },
  {
    title: '办结归档',
    description: '预警处理完成',
    time: '',
    status: 'wait',
    user: ''
  }
];

// 预警趋势图数据配置
const trendChartOption = {
  title: {
    text: '近30天预警趋势',
    left: 'center'
  },
  tooltip: {
    trigger: 'axis'
  },
  legend: {
    data: ['紧急', '重要', '一般'],
    bottom: 0
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '10%',
    top: '15%',
    containLabel: true
  },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: Array.from({length: 30}, (_, i) => `${i+1}日`)
  },
  yAxis: {
    type: 'value'
  },
  series: [
    {
      name: '紧急',
      type: 'line',
      stack: '总量',
      data: Array.from({length: 30}, () => Math.floor(Math.random() * 5)),
      itemStyle: {
        color: '#f5222d'
      },
      areaStyle: {
        opacity: 0.1
      }
    },
    {
      name: '重要',
      type: 'line',
      stack: '总量',
      data: Array.from({length: 30}, () => Math.floor(Math.random() * 10 + 5)),
      itemStyle: {
        color: '#fa8c16'
      },
      areaStyle: {
        opacity: 0.1
      }
    },
    {
      name: '一般',
      type: 'line',
      stack: '总量',
      data: Array.from({length: 30}, () => Math.floor(Math.random() * 15 + 10)),
      itemStyle: {
        color: '#1890ff'
      },
      areaStyle: {
        opacity: 0.1
      }
    }
  ]
};

// 预警分布图数据配置
const distributionChartOption = {
  title: {
    text: '预警类型分布',
    left: 'center'
  },
  tooltip: {
    trigger: 'item'
  },
  legend: {
    orient: 'vertical',
    left: 'left',
  },
  series: [
    {
      name: '预警类型',
      type: 'pie',
      radius: '60%',
      data: [
        { value: 35, name: '指标异常' },
        { value: 25, name: '投诉反馈' },
        { value: 20, name: '舆情监测' },
        { value: 15, name: '系统预警' },
        { value: 5, name: '其他' }
      ],
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }
  ]
};

const WarningCenter = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedWarning, setSelectedWarning] = useState(mockWarnings[0]);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    type: 'all',
    level: 'all',
    status: 'all',
    timeRange: null
  });
  
  // 过滤预警列表
  const getFilteredWarnings = () => {
    return mockWarnings.filter(item => {
      // 根据状态标签筛选
      if (activeTab !== 'all' && activeTab !== item.status) {
        return false;
      }
      // 根据其他筛选条件
      if (searchParams.keyword && !item.title.includes(searchParams.keyword)) {
        return false;
      }
      if (searchParams.type !== 'all' && searchParams.type !== item.type) {
        return false;
      }
      if (searchParams.level !== 'all' && searchParams.level !== item.level) {
        return false;
      }
      if (searchParams.status !== 'all' && searchParams.status !== item.status) {
        return false;
      }
      return true;
    });
  };
  
  // 处理搜索参数变更
  const handleSearchChange = (field, value) => {
    setSearchParams({
      ...searchParams,
      [field]: value
    });
  };
  
  // 根据预警级别返回标签颜色
  const getLevelTagColor = (level) => {
    const colors = {
      urgent: 'red',
      high: 'orange',
      normal: 'blue'
    };
    return colors[level] || 'blue';
  };
  
  // 根据处理状态返回标签
  const getStatusTag = (status) => {
    switch (status) {
      case 'pending':
        return <Tag icon={<ClockCircleOutlined />} color="default">待受理</Tag>;
      case 'processing':
        return <Tag icon={<SyncOutlined spin />} color="processing">处理中</Tag>;
      case 'done':
        return <Tag icon={<CheckCircleOutlined />} color="success">已办结</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };
  
  // 渲染预警详情
  const renderWarningDetail = () => {
    if (!selectedWarning) {
      return (
        <div className="warning-empty-detail">
          <BellOutlined />
          <p>请选择左侧预警查看详情</p>
        </div>
      );
    }
    
    return (
      <div className="warning-detail">
        <div className="warning-detail-header">
          <div className="warning-detail-title">
            <Tag color={getLevelTagColor(selectedWarning.level)}>
              {selectedWarning.level === 'urgent' ? '紧急' : 
               selectedWarning.level === 'high' ? '重要' : '一般'}
            </Tag>
            <h2>{selectedWarning.title}</h2>
          </div>
          <div className="warning-detail-actions">
            <Space>
              <Button icon={<ShareAltOutlined />}>分享</Button>
              <Button icon={<DownloadOutlined />}>导出</Button>
              <Button type="primary" icon={<SolutionOutlined />}>处理</Button>
            </Space>
          </div>
        </div>
        
        <div className="warning-detail-meta">
          <div className="warning-meta-item">
            <span className="meta-label">预警类型：</span>
            <span className="meta-value">{selectedWarning.type}</span>
          </div>
          <div className="warning-meta-item">
            <span className="meta-label">所属区域：</span>
            <span className="meta-value">{selectedWarning.area}</span>
          </div>
          <div className="warning-meta-item">
            <span className="meta-label">发生时间：</span>
            <span className="meta-value">{selectedWarning.createTime}</span>
          </div>
          <div className="warning-meta-item">
            <span className="meta-label">处理状态：</span>
            <span className="meta-value">{getStatusTag(selectedWarning.status)}</span>
          </div>
          <div className="warning-meta-item">
            <span className="meta-label">责任部门：</span>
            <span className="meta-value">{selectedWarning.department}</span>
          </div>
        </div>
        
        <Divider />
        
        <div className="warning-detail-content">
          <Tabs defaultActiveKey="detail">
            <TabPane tab="预警详情" key="detail">
              <div className="warning-detail-section">
                <h3>问题描述</h3>
                <p>{selectedWarning.desc}</p>
              </div>
              <div className="warning-detail-section">
                <h3>影响分析</h3>
                <p>{selectedWarning.influence}</p>
              </div>
              <div className="warning-detail-section">
                <h3>处置建议</h3>
                <p>{selectedWarning.suggestion}</p>
              </div>
              
              <div className="warning-detail-section">
                <h3>相关趋势</h3>
                <ReactECharts 
                  option={{
                    title: {
                      text: '指标异常趋势',
                      left: 'center',
                      top: 0
                    },
                    tooltip: {
                      trigger: 'axis'
                    },
                    xAxis: {
                      type: 'category',
                      data: ['6月', '7月', '8月', '9月', '10月']
                    },
                    yAxis: {
                      type: 'value',
                      name: '企业开办时长(天)'
                    },
                    series: [
                      {
                        data: [3.1, 3.3, 3.2, 3.8, 5.7],
                        type: 'line',
                        smooth: true,
                        markPoint: {
                          data: [
                            { type: 'max', name: '最大值' }
                          ]
                        },
                        markLine: {
                          data: [
                            { type: 'average', name: '平均值' },
                            { yAxis: 4, name: '警戒线', lineStyle: { color: '#ff4d4f' } }
                          ]
                        }
                      }
                    ]
                  }}
                  style={{ height: 300 }}
                />
              </div>
            </TabPane>
            <TabPane tab="处理流程" key="process">
              <div className="warning-process">
                <Steps direction="vertical" current={2}>
                  {mockProcessSteps.map((step, index) => (
                    <Step
                      key={index}
                      title={step.title}
                      description={
                        <div className="step-description">
                          <div>{step.description}</div>
                          {step.time && <div className="step-time">{step.time}</div>}
                          {step.user && <div className="step-user">处理人: {step.user}</div>}
                        </div>
                      }
                      status={step.status}
                    />
                  ))}
                </Steps>
              </div>
            </TabPane>
            <TabPane tab="关联影响" key="related">
              <div className="warning-related">
                <h3>受影响企业</h3>
                <div className="affected-stats">
                  <div className="affected-item">
                    <div className="affected-num">125</div>
                    <div className="affected-label">企业总数</div>
                  </div>
                  <div className="affected-item">
                    <div className="affected-num">37</div>
                    <div className="affected-label">重点企业</div>
                  </div>
                  <div className="affected-item">
                    <div className="affected-num">88</div>
                    <div className="affected-label">中小企业</div>
                  </div>
                </div>
                
                <h3>行业分布</h3>
                <div className="industry-distribution">
                  <div className="industry-item">
                    <span className="industry-name">制造业</span>
                    <Progress percent={45} strokeColor="#1890ff" />
                  </div>
                  <div className="industry-item">
                    <span className="industry-name">批发零售</span>
                    <Progress percent={28} strokeColor="#52c41a" />
                  </div>
                  <div className="industry-item">
                    <span className="industry-name">信息技术</span>
                    <Progress percent={15} strokeColor="#fa8c16" />
                  </div>
                  <div className="industry-item">
                    <span className="industry-name">其他行业</span>
                    <Progress percent={12} strokeColor="#722ed1" />
                  </div>
                </div>
              </div>
            </TabPane>
          </Tabs>
        </div>
      </div>
    );
  };

  return (
    <div className="warning-container">
      <div className="warning-header">
        <h1>预警中心</h1>
        <div className="warning-overview">
          <div className="overview-item">
            <Badge count={5} overflowCount={99}>
              <div className="overview-icon urgent">
                <FireOutlined />
              </div>
            </Badge>
            <div className="overview-info">
              <div className="overview-title">紧急预警</div>
              <div className="overview-value">5</div>
            </div>
          </div>
          <div className="overview-item">
            <Badge count={12} overflowCount={99}>
              <div className="overview-icon high">
                <ExclamationCircleOutlined />
              </div>
            </Badge>
            <div className="overview-info">
              <div className="overview-title">重要预警</div>
              <div className="overview-value">12</div>
            </div>
          </div>
          <div className="overview-item">
            <Badge count={24} overflowCount={99}>
              <div className="overview-icon normal">
                <BellOutlined />
              </div>
            </Badge>
            <div className="overview-info">
              <div className="overview-title">一般预警</div>
              <div className="overview-value">24</div>
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-icon trend">
              <LineChartOutlined />
            </div>
            <div className="overview-info">
              <div className="overview-title">环比变化</div>
              <div className="overview-value">+12.5%</div>
            </div>
          </div>
        </div>
      </div>
      
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card className="warning-card">
            <div className="warning-search">
              <Input 
                placeholder="搜索预警标题" 
                prefix={<SearchOutlined />} 
                allowClear
                value={searchParams.keyword}
                onChange={e => handleSearchChange('keyword', e.target.value)}
              />
              <div className="warning-filter">
                <Select 
                  placeholder="预警类型" 
                  style={{ width: '48%' }} 
                  value={searchParams.type}
                  onChange={value => handleSearchChange('type', value)}
                >
                  <Option value="all">全部类型</Option>
                  <Option value="指标异常">指标异常</Option>
                  <Option value="投诉反馈">投诉反馈</Option>
                  <Option value="舆情监测">舆情监测</Option>
                  <Option value="系统预警">系统预警</Option>
                </Select>
                <Select 
                  placeholder="紧急程度" 
                  style={{ width: '48%' }} 
                  value={searchParams.level}
                  onChange={value => handleSearchChange('level', value)}
                >
                  <Option value="all">全部级别</Option>
                  <Option value="urgent">紧急</Option>
                  <Option value="high">重要</Option>
                  <Option value="normal">一般</Option>
                </Select>
              </div>
            </div>
            
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab}
              className="warning-tabs"
            >
              <TabPane tab="全部" key="all" />
              <TabPane 
                tab={<Badge count={2} offset={[10, 0]}>待受理</Badge>} 
                key="pending" 
              />
              <TabPane 
                tab={<Badge count={2} offset={[10, 0]}>处理中</Badge>} 
                key="processing" 
              />
              <TabPane tab="已办结" key="done" />
            </Tabs>
            
            <List
              className="warning-list"
              dataSource={getFilteredWarnings()}
              renderItem={item => (
                <List.Item 
                  onClick={() => setSelectedWarning(item)}
                  className={selectedWarning?.id === item.id ? 'warning-item-active' : ''}
                >
                  <div className="warning-item">
                    <div className="warning-item-header">
                      <Tag color={getLevelTagColor(item.level)}>
                        {item.type}
                      </Tag>
                      <span className="warning-item-time">{item.createTime.split(' ')[0]}</span>
                    </div>
                    <div className="warning-item-title">{item.title}</div>
                    <div className="warning-item-meta">
                      <span className="warning-item-area">{item.area}</span>
                      {getStatusTag(item.status)}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>
          
          <Card title="预警统计" className="warning-card">
            <Tabs defaultActiveKey="trend">
              <TabPane tab="趋势分析" key="trend">
                <ReactECharts 
                  option={trendChartOption} 
                  style={{ height: 300 }}
                />
              </TabPane>
              <TabPane tab="类型分布" key="distribution">
                <ReactECharts 
                  option={distributionChartOption} 
                  style={{ height: 300 }}
                />
              </TabPane>
            </Tabs>
          </Card>
        </Col>
        
        <Col xs={24} lg={16}>
          <Card className="warning-card warning-detail-card">
            {renderWarningDetail()}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default WarningCenter; 